import { compose } from "@ally/utilitarian";
import { AccountLOB, AccountStatus, AccountType, AccountTypeToLabel, } from "../types";
import { InvestAccountName, DepositAccountName, CCAccountName, } from "../../customization/types";
import { ownershipTypes, blockedLiveChatAlertCodesForDropdown, blockedProductSubTypes, blockedBankruptcyCodes, } from "./constants";
/**
 * Checks if the close date of an account was more than 60 days ago and returns true if it is
 *
 * @param closeDate Takes any param that extends Account and deconstructs it to get the close date
 * @returns Returns false if an account's close date was less than 60 days ago and true otherwise
 */
export const filterExpiredAccount = ({ closeDate, }) => {
    if (!closeDate) {
        return true;
    }
    const today = new Date();
    const dayDifference = today.getDate() - 60;
    const unixSixtyDaysAgo = today.setDate(dayDifference);
    const sixtyDaysAgo = new Date(unixSixtyDaysAgo).getTime();
    const isMoreThan60 = new Date(closeDate).getTime() < sixtyDaysAgo;
    return !isMoreThan60;
};
/**
 * Determines if an Account has a specified AccountType
 *
 * @param accTypes Array of Account Types used to match with array of accounts
 * @returns Returns boolean value, does account match account type
 *
 * @example
 * ```ts
 * getByType(AccountType.DDA, AccountType.MMA, AccountType.CMG)
 * ```
 */
export const getByType = (...accTypes) => {
    return (accounts) => {
        return accounts.filter(({ type }) => {
            const hasType = accTypes.includes(type);
            return hasType;
        });
    };
};
/**
 * Sorts Account's in order by specified Account Types
 *
 * @param accountTypes Array of account types, index will be used to determine account order
 * @returns Returns a number, which suggests the order of Accounts
 *
 * @example
 * ```ts
 * sortByAccountType([
 *   AccountType.INVS,
 *   AccountType.INVW,
 *   AccountType.INVA
 *   ])
 * ```
 */
export const sortByAccountType = (accountTypes) => {
    return (extraSort) => {
        return (a, b) => {
            const aAcctType = a.type;
            const bAcctType = b.type;
            const aIndex = accountTypes.indexOf(aAcctType);
            const bIndex = accountTypes.indexOf(bAcctType);
            const order = aIndex - bIndex;
            if (extraSort && order === 0) {
                // istanbul ignore next
                return extraSort(a, b);
            }
            return order;
        };
    };
};
/**
 * Retrieves the current deposit account balance
 *
 * @param type Account type used to check if it is a deposit account
 * @param balance Account balance used to check the current or available balance
 * @returns Returns a number, deposit account balance, defaults to 0
 *
 * @example
 * ```ts
 * determineDepositAccountBalance({
 *   type: someAccount.type,
 *   balance: someAccount.balance
 *   })
 * ```
 */
const determineDepositAccountBalance = ({ type, balance, }) => {
    const amount = type === AccountType.CDA ? balance === null || balance === void 0 ? void 0 : balance.current : balance === null || balance === void 0 ? void 0 : balance.available;
    return amount !== null && amount !== void 0 ? amount : 0;
};
/**
 *  Checks the ownership type of an account
 *
 * @param ownershipType Ownership type of an account
 * @returns Returns a boolean value, is ownership type valid
 *
 */
export const checkOwnershipType = ({ ownershipType = "", }) => {
    return ownershipTypes.includes(ownershipType);
};
/**
 * Filter Account's by whether or not they are or are not a retirement account
 *
 * @param isRetirementAccount Used to check if account retirement indicator is equal to this
 * @returns Returns boolean value, which determines if account retirment indicator matches isRetirmentAccount
 *
 * @example
 * ```ts
 * .filter(filterByRetirementIndicator(true))
 * ```
 */
export const filterByRetirementIndicator = (isRetirementAccount) => (account) => {
    var _a;
    return !!((_a = account.indicator) === null || _a === void 0 ? void 0 : _a.retirement) === isRetirementAccount;
};
/**
 * Sort Accounts on whether or not they are closed
 *
 * @param extraSort Account sort used to sort two accounts
 * @returns Returns number, which is index used to order accounts
 */
export const sortByStatus = (extraSort) => (a, b) => {
    const getIsClosed = (account) => !!account.status.match(/closed/i);
    const aIsClosed = getIsClosed(a);
    const bIsClosed = getIsClosed(b);
    if (aIsClosed && !bIsClosed) {
        // istanbul ignore next
        return 1;
    }
    if (!aIsClosed && bIsClosed) {
        // istanbul ignore next
        return -1;
    }
    if (extraSort) {
        return extraSort(a, b);
    }
    // istanbul ignore next
    return 0;
};
/**
 * Sorts accounts by the day they were opened
 *
 * @param extraSort AccountSort<Account> which will sort two accounts
 * @returns Returns number used to sort the accounts
 */
const sortByOpenDate = (extraSort) => (a, b) => {
    const getOpenDate = ({ openDate }) => {
        // return 0 if open date is somehow undefined, this will sort the account to the bottom of a list
        return openDate ? new Date(openDate).getTime() : 0;
    };
    const aOpenDate = getOpenDate(a);
    const bOpenDate = getOpenDate(b);
    const order = aOpenDate - bOpenDate;
    if (extraSort && order === 0) {
        return extraSort(a, b);
    }
    return order;
};
const sortByStatusAndOpenDate = compose(sortByStatus, sortByOpenDate)();
/**
 * gets account retirement plan type
 *
 * @param acct Takes an Account which is used to obtain the retirement plan type
 * @returns Returns the plan type in all upper case
 */
const getPlanType = (acct) => {
    var _a;
    return (((_a = acct.meta) === null || _a === void 0 ? void 0 : _a.retirementPlanType) || "").toUpperCase();
};
/**
 * Sorts a group of accounts by retirement plan
 *
 * @param retirementPlans Takes an array of strings
 * @returns Returns a number used to sort the Accounts
 */
const sortByRetirementPlan = (retirementPlans) => {
    return (extraSort) => {
        return (a, b) => {
            const aRetPlan = getPlanType(a);
            const bRetPlan = getPlanType(b);
            const aIndex = retirementPlans.indexOf(aRetPlan);
            const bIndex = retirementPlans.indexOf(bRetPlan);
            const order = aIndex - bIndex;
            if (extraSort && order === 0) {
                return extraSort(a, b);
            }
            return order;
        };
    };
};
/**
 * Orders accounts by deposit account names
 *
 * @param customizationOpts Optional, takes a CustomizationOptions type
 * @returns Returns indexes used for order
 */
const getCustomDepositAccountOrder = (customizationOpts) => {
    const orderedAccounts = [];
    customizationOpts === null || customizationOpts === void 0 ? void 0 : customizationOpts.forEach(({ accountTypes }) => {
        accountTypes.forEach(({ lob, name, order }) => {
            if (lob === "DEPOSIT" && name in DepositAccountName) {
                orderedAccounts.push({ name: name, order });
            }
        });
    });
    orderedAccounts.sort((a, b) => a.order - b.order);
    return orderedAccounts;
};
/**
 * Sorts deposit accounts
 *
 * @param accountsToSort Array of deposit accounts that will be sorted based on customization options
 * @param customizationOpts Optional, takes array of customization options
 * @returns Returns a sorted array of deposits accounts
 */
const sortDepositAccounts = (accountsToSort, customizationOpts) => {
    getCustomDepositAccountOrder(customizationOpts).forEach(({ name, order }) => {
        accountsToSort[name].order = order;
    });
    /**
     * Accounts without an order value are required to be placed at the front
     * of the array in their default order. Hence the `|| 0` to give `undefined`
     * order values a lower value in the sort order than defined values, which
     * are indexed from 1.
     */
    return Object.values(accountsToSort)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map(({ acct }) => acct);
};
/**
 * Maps account type label, account balance to show, and account nickname, to be used for dropdown, deposit accounts filtered
 *
 * @param account Account that will be returned as an account for dropdown
 * @returns returns account for dropdown
 */
const mapDepositAccountsForDropdown = (account) => {
    const balanceNumber = determineDepositAccountBalance({
        type: account.type,
        balance: account.balance,
    });
    return Object.assign(Object.assign({}, account), { nickName: account.nickName || account.name, accountTypeLabel: AccountTypeToLabel[account.type], accountBalanceToShow: balanceNumber });
};
/**
 * Sorts multiple different accounts based on customization options and returns accounts formatted for dropdown
 *
 * @param ddaAccts Array of accounts -- checking
 * @param mmaAccts Array of accounts -- savings
 * @param sdaAccts Array of accounts -- savings
 * @param cmgAccts Array of accounts -- savings
 * @param cdaAccts Array of accounts -- cd
 * @param retAccts Array of accounts -- ira
 * @param customizationOpts Optional, array of customization options
 * @returns Array of accounts for dropdown
 */
export const formatDepositAccountsForDropdown = (ddaAccts, mmaAccts, sdaAccts, cmgAccts, cdaAccts, retAccts, customizationOpts) => {
    // CHECKING ACCOUNTS
    const interestCheckingGroup = (ddaAccts || [])
        .filter(filterExpiredAccount)
        .sort(sortByStatusAndOpenDate);
    // SAVINGS ACCOUNTS
    const otherDepAccountsGroup = [
        ...(mmaAccts || []),
        ...(sdaAccts || []),
        ...(cmgAccts || []),
    ]
        .filter(filterExpiredAccount)
        .sort(compose(sortByStatus, sortByAccountType([AccountType.MMA, AccountType.SDA, AccountType.CMG]), sortByOpenDate)());
    // CD ACCOUNTS
    const cdGroup = (cdaAccts || [])
        .filter(filterExpiredAccount)
        .sort(sortByStatusAndOpenDate);
    // IRA ACCOUNTS
    const retirementGroup = (retAccts || [])
        .filter(filterExpiredAccount)
        .sort(compose(sortByRetirementPlan(["SEP", "ROTH", "TRADITIONAL"]), sortByStatus, sortByAccountType([AccountType.SDA, AccountType.CDA]), sortByOpenDate)());
    const sortedAccounts = sortDepositAccounts({
        [DepositAccountName.CHECKING]: { acct: interestCheckingGroup },
        [DepositAccountName.SAVINGS]: { acct: otherDepAccountsGroup },
        [DepositAccountName.CD]: { acct: cdGroup },
        [DepositAccountName.IRA]: { acct: retirementGroup },
    }, customizationOpts);
    return sortedAccounts.flat().map(mapDepositAccountsForDropdown);
};
/**
 * Maps account type label, account balance to show, and account nickname, to be used for dropdown, invest accounts filtered
 *
 * @param account Account that will be returned as an account for dropdown
 * @returns returns account for dropdown
 */
export const mapInvestAccountsForDropdown = (account) => {
    var _a, _b;
    return Object.assign(Object.assign({}, account), { nickName: account.nickName || account.name, accountTypeLabel: AccountTypeToLabel[account.type], accountBalanceToShow: (_b = (_a = account.value) === null || _a === void 0 ? void 0 : _a.total) !== null && _b !== void 0 ? _b : 0 });
};
/**
 * Order Invest accounts in an order provided by customization options
 *
 * @param customizationOpts Optional, array of customization options
 * @returns Array of invest accounts
 */
const getCustomInvestAccountOrder = (customizationOpts) => {
    const orderedAccounts = [];
    customizationOpts === null || customizationOpts === void 0 ? void 0 : customizationOpts.forEach(({ accountTypes }) => {
        accountTypes.forEach(({ lob, name, order }) => {
            if (lob === "INVEST" && name in InvestAccountName) {
                orderedAccounts.push({ name: name, order });
            }
        });
    });
    orderedAccounts.sort((a, b) => a.order - b.order);
    return orderedAccounts;
};
/**
 * Formats an account so it can be used for dropdown, filters accounts to invest account type
 *
 * @param accounts Array of accounts that will be formatted for dropdown
 * @param customizationOpts Optional, array of customization options used for sorting
 * @returns Retruns an array of accounts for dropdown
 */
export const formatInvestAccountsForDropdown = (accounts, customizationOpts) => {
    const accountOrder = getCustomInvestAccountOrder(customizationOpts);
    const orderedAccountNames = [
        // INVW - Wealth Portfolio
        AccountType.INVW,
        // INVA - Robo Portfolio
        AccountType.INVA,
        // INVS - Self-directed
        AccountType.INVS,
    ].sort((a, b) => {
        var _a, _b;
        return (((_a = accountOrder.find((acct) => acct.name === a.toString())) === null || _a === void 0 ? void 0 : _a.order) || 0) -
            (((_b = accountOrder.find((acct) => acct.name === b.toString())) === null || _b === void 0 ? void 0 : _b.order) || 0);
    });
    const investGroup = accounts.sort(compose(sortByAccountType(orderedAccountNames), sortByStatus)());
    return investGroup.map(mapInvestAccountsForDropdown);
};
/**
 * Maps account type label, account balance to show, and account nickname, to be used for dropdown, Mortgage accounts filtered
 *
 * @param account Account that will be returned as an account for dropdown
 * @returns returns account for dropdown
 */
export const mapMortgageAccountsForDropdown = (account) => {
    var _a, _b;
    return Object.assign(Object.assign({}, account), { nickName: account.nickName || account.name, accountTypeLabel: AccountTypeToLabel[account.type], accountBalanceToShow: (_b = (_a = account.meta) === null || _a === void 0 ? void 0 : _a.amountDue) !== null && _b !== void 0 ? _b : 0 });
};
/**
 * Sorts two accounts based on account number
 *
 * @param a Account or something that extends Account, will obtain account number from this
 * @param b Second account or something that extends Account, will obtain account number from this
 * @returns Returns a number, index of two accounts, which should come first in sorted array
 */
export const sortByAccountNumber = (a, b) => {
    return parseInt(a.accountNumber, 10) - parseInt(b.accountNumber, 10);
};
/**
 * Formats an account so it can be used for dropdown, filters accounts to mortgage and home equity account type
 *
 * @param accounts Array of accounts that will be formatted for dropdown
 * @returns Retruns an array of accounts for dropdown
 */
export const formatMortgageAccountsForDropdown = (accounts) => {
    const isMortgage = (a) => { var _a; return a.type === AccountType.MORTGAGE && !((_a = a.indicator) === null || _a === void 0 ? void 0 : _a.blocked); };
    const isHeloc = (a) => { var _a; return a.type === AccountType.HOME_EQUITY && !((_a = a.indicator) === null || _a === void 0 ? void 0 : _a.blocked); };
    const isActive = (a) => a.status === AccountStatus.ACTIVE;
    const isPaidOff = (a) => a.status === AccountStatus.PAIDOFF;
    const activeMortgageAccounts = accounts.filter((account) => isMortgage(account) && isActive(account));
    const paidOffMortgageAccounts = accounts.filter((account) => isMortgage(account) && isPaidOff(account));
    const activeHelocAccounts = accounts.filter((account) => isHeloc(account) && isActive(account));
    const paidOffHelocAccounts = accounts.filter((account) => isHeloc(account) && isPaidOff(account));
    const mortgageGroup = [
        activeMortgageAccounts,
        paidOffMortgageAccounts,
        activeHelocAccounts,
        paidOffHelocAccounts,
    ]
        .map((accts) => accts.sort(sortByAccountNumber))
        .flat();
    return mortgageGroup.map(mapMortgageAccountsForDropdown);
};
/**
 * Maps account type label, account balance to show, and account nickname, to be used for dropdown, credit card accounts filtered
 *
 * @param account Account that will be returned as an account for dropdown
 * @returns returns account for dropdown
 */
function mapCreditCardAccountsForDropdown(account) {
    var _a, _b;
    return Object.assign(Object.assign({}, account), { nickName: account.nickName || account.name, accountTypeLabel: AccountTypeToLabel[account.type], accountBalanceToShow: (_b = (_a = account.meta) === null || _a === void 0 ? void 0 : _a.paymentDue) !== null && _b !== void 0 ? _b : 0 });
}
/**
 * Depending on customization options, order credit card accounts in an array and return it
 *
 * @param customizationOpts Optional, array of customization options
 * @returns Array of account customization info, an ordered array of credit card accounts
 */
const getCustomCreditCardAccountOrder = (customizationOpts) => {
    const orderedAccounts = [];
    customizationOpts === null || customizationOpts === void 0 ? void 0 : customizationOpts.forEach(({ accountTypes }) => {
        accountTypes.forEach(({ lob, name, order }) => {
            if (lob === "CC" && name in CCAccountName) {
                orderedAccounts.push({ name: name, order });
            }
        });
    });
    orderedAccounts.sort((a, b) => a.order - b.order);
    return orderedAccounts;
};
/**
 * Formats an account so it can be used for dropdown, filters accounts to credit card account type
 *
 * @param accounts Array of accounts that will be formatted for dropdown
 * @param customizationOptions Array of customization options used to order account names
 * @returns Retruns an array of accounts for dropdown
 */
export const formatCreditCardAccountsForDropdown = (accounts, customizationOptions) => {
    const accountOrder = getCustomCreditCardAccountOrder(customizationOptions);
    const orderedAccountNames = [
        AccountType.PLATINUM,
        AccountType.EVERYDAY,
        AccountType.UNLIMITED,
    ].sort((a, b) => {
        var _a, _b;
        return (((_a = accountOrder.find((acct) => acct.name === a.toString())) === null || _a === void 0 ? void 0 : _a.order) || 0) -
            (((_b = accountOrder.find((acct) => acct.name === b.toString())) === null || _b === void 0 ? void 0 : _b.order) || 0);
    });
    const creditCardAccounts = accounts.sort(compose(sortByAccountType(orderedAccountNames)
    // sortByStatus // TODO may need this if we show closed CC accounts
    )());
    return creditCardAccounts.map(mapCreditCardAccountsForDropdown);
};
/**
 * Checks an account to see if it was paid in full in the last 45 days
 *
 * @param a Account to check if it was paid in full
 * @returns Returns a boolean value, true if it was paid in full in the last 45 days
 */
const isAllowedPaidOffAccount = (a) => {
    var _a, _b;
    const cutOffDayInMs = new Date(Date.now() - 45 * 24 * 60 * 60 * 1000);
    if ((_a = a.meta) === null || _a === void 0 ? void 0 : _a.paidInFullOrZeroBalance) {
        const isLessThanFortyFive = new Date((_b = a.meta) === null || _b === void 0 ? void 0 : _b.paidInFullDate) >= cutOffDayInMs;
        // Only show paid in full account if paid in full in the last 45 days
        return isLessThanFortyFive;
    }
    return true;
};
/**
 * Filter array of accounts that will be shown on the dashboard, auto account type
 *
 * @param accounts Array of accounts that will be filtered
 * @param isBankruptcyAccepted Should bankruptcy be accepted
 * @returns
 */
export const filterAutoAccountsForDashboard = (accounts, isBankruptcyAccepted) => {
    const isAllowedBankruptcyAccount = (a, hasBankruptcyAccepted) => {
        var _a, _b;
        if (hasBankruptcyAccepted)
            return true;
        return !((_b = (_a = a.meta) === null || _a === void 0 ? void 0 : _a.bankruptcyCodes) === null || _b === void 0 ? void 0 : _b.some((bankruptcyCode) => blockedBankruptcyCodes.includes(bankruptcyCode)));
    };
    const autoAccountsForDashboard = accounts.filter((account) => isAllowedBankruptcyAccount(account, isBankruptcyAccepted) &&
        isAllowedPaidOffAccount(account));
    return autoAccountsForDashboard;
};
/**
 * Maps account type label, account balance to show, and account nickname, to be used for dropdown, auto accounts filtered
 *
 * @param account Account that will be returned as an account for dropdown
 * @returns returns account for dropdown
 */
export const mapAutoAccountsForDropdown = (account) => {
    var _a, _b;
    return Object.assign(Object.assign({}, account), { nickName: account.nickName || account.name, accountTypeLabel: AccountTypeToLabel[account.type], accountBalanceToShow: (_b = (_a = account.meta) === null || _a === void 0 ? void 0 : _a.nextPayment) === null || _b === void 0 ? void 0 : _b.totalAmountDue });
};
/**
 * Formats an account so it can be used for dropdown, filters accounts to auto account type
 *
 * @param accounts Array of accounts that will be formatted for dropdown
 * @returns Retruns an array of accounts for dropdown
 */
export const formatAutoAccountsForDropdown = (accounts) => {
    const isAuto = (a) => a.lob === AccountLOB.AUTO;
    const isAllowedLiveChatAlertCodes = (a) => {
        var _a, _b;
        return (_b = (_a = a.meta) === null || _a === void 0 ? void 0 : _a.liveChatAlertCodes) === null || _b === void 0 ? void 0 : _b.every((code) => !blockedLiveChatAlertCodesForDropdown.includes(code));
    };
    const isAllowedSubType = (a) => {
        var _a;
        if (!blockedProductSubTypes.includes((_a = a.meta) === null || _a === void 0 ? void 0 : _a.productSubtype)) {
            return true;
        }
        return false;
    };
    const autoAccountsForDropdown = accounts.filter((account) => isAuto(account) &&
        isAllowedLiveChatAlertCodes(account) &&
        isAllowedSubType(account) &&
        isAllowedPaidOffAccount(account));
    return autoAccountsForDropdown.map(mapAutoAccountsForDropdown);
};
//# sourceMappingURL=utils.js.map